import axios from 'axios';
import Cookie from 'js-cookie';
import store from '../store';
import { message } from 'ant-design-vue';
import i18n from '@/language/i18n';

// 跨域认证信息 header 名
const xsrfHeaderName = 'token';

axios.defaults.timeout = 1 * 60 * 1000;
axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = xsrfHeaderName;
axios.defaults.xsrfCookieName = xsrfHeaderName;
// 认证类型
const AUTH_TYPE = {
	BEARER: 'Bearer',
	BASIC: 'basic',
	AUTH1: 'auth1',
	AUTH2: 'auth2'
};

// http method
const METHOD = {
	GET: 'get',
	POST: 'post'
};
//存在对Electron应用的特殊处理
// 定义允许在游客模式下访问的接口白名单（可以是部分路径）
const GUEST_ALLOWED_PATHS = [
	'/api/tem.web_api/getPlatformList',
	'/api/tem.app_public/getCidTempList',
	'/api/tem.app_member/getMemberUser',
	'/api/tem.new_class/searchList',
	'/api/tem.app_models/getNweModel',
	'/api/tem.app_public/getSharedClass',
	'/api/tem.app_shared/getLogClass',
	'/api/tem.app_shared/getLogList',
	'/api/tem.app_shared/getToken'
];
let lastNetworkErrorTime = 0;
/**
 * axios请求
 * @param url 请求地址
 * @param method {METHOD} http method
 * @param params 请求参数
 * @returns {Promise<AxiosResponse<T>>}
 */
async function request(url, params, method, config) {
	try {
		if (process.env.VUE_APP_VERSION == 'electron') {
			if (store.state.isGuest) {
				const allowed = GUEST_ALLOWED_PATHS.some(fragment => url.includes(fragment));
				if (!allowed) throw new Error('游客模式');
			}
			if (!store.state.online) {
				const now = Date.now();
				if (now - lastNetworkErrorTime > 2000) {
					message.error(i18n.$t(`tips.tip132`));
					lastNetworkErrorTime = now;
				}
				throw new Error('网络不可用');
			}
		}

		if (!config) {
			config = { custom: { loading: true } };
		} else if (!config.custom || !config.custom.hasOwnProperty('loading')) {
			config.custom.loading = true;
		}

		const token = getAuthorization(AUTH_TYPE.BEARER);
		if (token) {
			config.headers = config.headers || {};
			config.headers['token'] = `${token}`; // 将 token 加入到请求头
		}

		// config = config ? config : {custom:{loading:true}}
		switch (method) {
			case METHOD.GET:
				return axios.get(url, { params, ...config });
			case METHOD.POST:
				return axios.post(url, params, config);
			default:
				return axios.get(url, { params, ...config });
		}
	} catch (error) {
		// 静默处理错误（不抛出到控制台）
		console.warn(error.message); // 可选：开发环境调试

		// 返回可识别的错误结构（保持接口一致性）
		return {
			error: true,
			code: 'REQUEST_ERROR',
			message: error.message,
			data: null
		};
	}
}

/**
 * 设置认证信息
 * @param auth {Object}
 * @param authType {AUTH_TYPE} 认证类型，默认：{AUTH_TYPE.BEARER}
 */
function setAuthorization(auth, authType = AUTH_TYPE.BEARER) {
	switch (authType) {
		case AUTH_TYPE.BEARER:
			Cookie.set(xsrfHeaderName, auth.token, { expires: auth.expireAt });
			// Cookie.set(xsrfHeaderName, 'Bearer ' + auth.token, {expires: auth.expireAt})
			break;
		case AUTH_TYPE.BASIC:
		case AUTH_TYPE.AUTH1:
		case AUTH_TYPE.AUTH2:
		default:
			break;
	}
}

/**
 * 移出认证信息
 * @param authType {AUTH_TYPE} 认证类型
 */
function removeAuthorization(authType = AUTH_TYPE.BEARER) {
	switch (authType) {
		case AUTH_TYPE.BEARER:
			Cookie.remove(xsrfHeaderName);
			break;
		case AUTH_TYPE.BASIC:
		case AUTH_TYPE.AUTH1:
		case AUTH_TYPE.AUTH2:
		default:
			break;
	}
}
//获取token
function getAuthorization(authType = AUTH_TYPE.BEARER) {
	if (process.env.VUE_APP_VERSION == 'electron') {
		let token = localStorage.getItem('token');
		if (token) {
			return token;
		} else {
			return false;
		}
	} else {
		switch (authType) {
			case AUTH_TYPE.BEARER:
				if (Cookie.get(xsrfHeaderName)) {
					return Cookie.get(xsrfHeaderName);
				}
				break;
			case AUTH_TYPE.BASIC:
			case AUTH_TYPE.AUTH1:
			case AUTH_TYPE.AUTH2:
			default:
				break;
		}
		return false;
	}
}

/**
 * 检查认证信息
 * @param authType
 * @returns {boolean}
 */
function checkAuthorization(authType = AUTH_TYPE.BEARER) {
	switch (authType) {
		case AUTH_TYPE.BEARER:
			if (Cookie.get(xsrfHeaderName)) {
				return true;
			}
			break;
		case AUTH_TYPE.BASIC:
		case AUTH_TYPE.AUTH1:
		case AUTH_TYPE.AUTH2:
		default:
			break;
	}
	return false;
}

/**
 * 加载 axios 拦截器
 * @param interceptors
 * @param options
 */
function loadInterceptors(interceptors, options) {
	const { request, response } = interceptors;
	// 加载请求拦截器
	request.forEach(item => {
		let { onFulfilled, onRejected } = item;
		if (!onFulfilled || typeof onFulfilled !== 'function') {
			onFulfilled = config => config;
		}
		if (!onRejected || typeof onRejected !== 'function') {
			onRejected = error => Promise.reject(error);
		}
		axios.interceptors.request.use(
			config => onFulfilled(config, options),
			error => onRejected(error, options)
		);
	});
	// 加载响应拦截器
	response.forEach(item => {
		let { onFulfilled, onRejected } = item;
		if (!onFulfilled || typeof onFulfilled !== 'function') {
			onFulfilled = response => response;
		}
		if (!onRejected || typeof onRejected !== 'function') {
			onRejected = error => Promise.reject(error);
		}
		axios.interceptors.response.use(
			response => onFulfilled(response, options),
			error => onRejected(error, options)
		);
	});
}

/**
 * 解析 url 中的参数
 * @param url
 * @returns {Object}
 */
function parseUrlParams(url) {
	const params = {};
	if (!url || url === '' || typeof url !== 'string') {
		return params;
	}
	const paramsStr = url.split('?')[1];
	if (!paramsStr) {
		return params;
	}
	const paramsArr = paramsStr.replace(/&|=/g, ' ').split(' ');
	for (let i = 0; i < paramsArr.length / 2; i++) {
		const value = paramsArr[i * 2 + 1];
		params[paramsArr[i * 2]] = value === 'true' ? true : value === 'false' ? false : value;
	}
	return params;
}

export { AUTH_TYPE, METHOD, checkAuthorization, getAuthorization, loadInterceptors, parseUrlParams, removeAuthorization, request, setAuthorization };
